import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Typography, 
  Space, 
  Switch, 
  Modal, 
  Form, 
  Input, 
  InputNumber, 
  message,
  Badge,
  Tooltip,
  Table
} from 'antd';
import { 
  DollarCircleOutlined,
  EditOutlined, 
  DeleteOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { ZarPackage, adminApi } from '../lib/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ZarPackages: React.FC = () => {
  const [editingPackage, setEditingPackage] = useState<ZarPackage | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch packages
  const { data: packages = [], isLoading } = useQuery<ZarPackage[]>({
    queryKey: ['zar-packages'],
    queryFn: () => adminApi.getZarPackages(),
  });

  // Create/Update mutation
  const updatePackageMutation = useMutation({
    mutationFn: ({ packageId, packageData }: { packageId?: string; packageData: Partial<ZarPackage> }) => {
      if (packageId) {
        return adminApi.updateZarPackage(packageId, packageData);
      } else {
        return adminApi.createZarPackage(packageData);
      }
    },
    onSuccess: () => {
      message.success('Package saved successfully');
      queryClient.invalidateQueries({ queryKey: ['zar-packages'] });
      setModalVisible(false);
      setEditingPackage(null);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error('Failed to save package: ' + (error.message || 'Unknown error'));
    }
  });

  // Delete mutation
  const deletePackageMutation = useMutation({
    mutationFn: (packageId: string) => adminApi.deleteZarPackage(packageId),
    onSuccess: () => {
      message.success('Package deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['zar-packages'] });
    },
    onError: (error: any) => {
      message.error('Failed to delete package: ' + (error.message || 'Unknown error'));
    }
  });

  // Watch price and discount to auto-calculate final_price
  const price = Form.useWatch('price', form);
  const discount = Form.useWatch('discount', form);

  useEffect(() => {
    if (price !== undefined && discount !== undefined) {
      const finalPrice = (price || 0) - (discount || 0);
      form.setFieldValue('final_price', finalPrice > 0 ? finalPrice : 0);
    }
  }, [price, discount, form]);

  const handleEdit = (pkg: ZarPackage) => {
    setEditingPackage(pkg);
    setModalVisible(true);
    form.setFieldsValue({
      zar_amount: pkg.zar_amount,
      price: pkg.price,
      discount: pkg.discount,
      final_price: pkg.final_price,
      display_name: pkg.display_name || '',
      description: pkg.description || '',
      is_active: pkg.is_active,
      sort_order: pkg.sort_order || 0
    });
  };

  const handleCreate = () => {
    setEditingPackage(null);
    setModalVisible(true);
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      // Ensure final_price is calculated
      if (values.final_price === undefined || values.final_price <= 0) {
        values.final_price = (values.price || 0) - (values.discount || 0);
      }

      updatePackageMutation.mutate({
        packageId: editingPackage?.id,
        packageData: values
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleToggleActive = (pkg: ZarPackage) => {
    updatePackageMutation.mutate({
      packageId: pkg.id,
      packageData: { is_active: !pkg.is_active }
    });
  };

  const handleDelete = (pkg: ZarPackage) => {
    Modal.confirm({
      title: 'Delete ZAR Package',
      content: `Are you sure you want to delete package "${pkg.display_name || `${pkg.zar_amount} ZAR`}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: () => deletePackageMutation.mutate(pkg.id)
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const columns = [
    {
      title: 'ZAR Amount',
      dataIndex: 'zar_amount',
      key: 'zar_amount',
      render: (amount: number) => (
        <Text strong style={{ fontSize: '16px' }}>
          {amount.toLocaleString()} ZAR
        </Text>
      ),
      sorter: (a: ZarPackage, b: ZarPackage) => a.zar_amount - b.zar_amount,
      width: 150,
    },
    {
      title: 'Original Price',
      dataIndex: 'price',
      key: 'price',
      render: (price: number, record: ZarPackage) => (
        <div>
          {record.discount > 0 ? (
            <Text delete type="secondary" style={{ fontSize: '14px' }}>
              {formatCurrency(price)}
            </Text>
          ) : (
            <Text style={{ fontSize: '14px' }}>{formatCurrency(price)}</Text>
          )}
        </div>
      ),
      sorter: (a: ZarPackage, b: ZarPackage) => a.price - b.price,
      width: 140,
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
      render: (discount: number) => discount > 0 ? (
        <Text type="success" strong style={{ fontSize: '14px' }}>
          -{formatCurrency(discount)}
        </Text>
      ) : (
        <Text type="secondary">—</Text>
      ),
      sorter: (a: ZarPackage, b: ZarPackage) => a.discount - b.discount,
      width: 130,
    },
    {
      title: 'Final Price',
      dataIndex: 'final_price',
      key: 'final_price',
      render: (price: number) => (
        <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
          {formatCurrency(price)}
        </Text>
      ),
      sorter: (a: ZarPackage, b: ZarPackage) => a.final_price - b.final_price,
      width: 130,
    },
    {
      title: 'Sort Order',
      dataIndex: 'sort_order',
      key: 'sort_order',
      sorter: (a: ZarPackage, b: ZarPackage) => (a.sort_order || 0) - (b.sort_order || 0),
      width: 110,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean, record: ZarPackage) => (
        <Badge 
          status={isActive ? 'success' : 'default'} 
          text={isActive ? 'Active' : 'Inactive'} 
        />
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value: any, record: ZarPackage) => record.is_active === value,
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 140,
      render: (_: any, record: ZarPackage) => (
        <Space>
          <Tooltip title={record.is_active ? 'Deactivate' : 'Activate'}>
            <Switch
              checked={record.is_active}
              onChange={() => handleToggleActive(record)}
              loading={updatePackageMutation.isPending}
              size="small"
            />
          </Tooltip>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <div>
              <Title level={3} style={{ margin: 0 }}>
                <DollarCircleOutlined style={{ marginRight: '8px' }} />
                ZAR Packages
              </Title>
              <Text type="secondary">Manage ZAR credit packages for purchase</Text>
            </div>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Add Package
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={packages}
          loading={isLoading}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} packages`,
          }}
        />
      </Card>

      {/* Edit/Create Modal */}
      <Modal
        title={editingPackage ? `Edit Package` : 'Create Package'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          form.resetFields();
          setModalVisible(false);
          setEditingPackage(null);
        }}
        confirmLoading={updatePackageMutation.isPending}
        width={600}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="ZAR Amount"
                name="zar_amount"
                rules={[{ required: true, message: 'Please enter ZAR amount' }, { type: 'number', min: 1 }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  step={10}
                  placeholder="100"
                  addonAfter="ZAR"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Price"
                name="price"
                rules={[{ required: true, message: 'Please enter price' }, { type: 'number', min: 0 }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  placeholder="10.00"
                  addonBefore="$"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Discount"
                name="discount"
                rules={[{ type: 'number', min: 0 }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  placeholder="0.00"
                  addonBefore="- $"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Final Price"
                name="final_price"
                rules={[{ required: true, message: 'Please enter final price' }, { type: 'number', min: 0 }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  placeholder="Auto-calculated"
                  addonBefore="$"
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Display Name"
            name="display_name"
            help="Optional custom name for the package (e.g., 'Starter Pack')"
          >
            <Input placeholder="Starter Pack" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <TextArea rows={2} placeholder="Optional description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Sort Order"
                name="sort_order"
                rules={[{ type: 'number', min: 0 }]}
              >
                <InputNumber style={{ width: '100%' }} min={0} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Active"
                name="is_active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default ZarPackages;

